import { useCallback } from 'react';
import { isEmpty } from '../../../../helpers/common';
import { CommentFilesInterface } from '../../../../interfaces/TaskInterface';
import { CHAT_GROUP_TYPE } from '../../../../global/constants';
import { ChatGroupInterface, MessageReplyInterface } from '../../../../interfaces/chatMessageInterface';
import { ReplyMessageDataInterface } from '../../../../interfaces/MessageInterface';
import { UserModel } from '../../../../mobx/models/user';
import { WorkspaceModel } from '../../../../mobx/models/workspace';
import { ProjectModel } from '../../../../mobx/models/project';

interface SendMessagePayload {
  Message: string;
  ProjectId?: string;
  ReadUsers?: (string | undefined)[];
  UploadedFiles: CommentFilesInterface[];
  Reply: ReplyMessageDataInterface | null;
  MessageGroupId?: string;
  CompanyId?: string;
  _id?: string;
}

interface UpdateMessagePayload {
  Message: string;
  UploadedFiles: CommentFilesInterface[];
  ProjectId?: string;
  MessageGroupId?: string;
}

interface OptimisticMessagePayload {
  projectId: string | null;
  fileExt: string;
  messageGroupId: null;
  sender: {
    avatar: {
      avatar: string;
      avatar_120: string;
    };
    email: string;
    id: string;
    name: string;
    phone: string;
    profile_photo: string;
    isAdmin: boolean;
    isOwner: boolean;
  };
  message: string;
  createdOn: string;
  isRead: boolean;
  uploadedFile: CommentFilesInterface[];
  reactions: never[];
  reply: MessageReplyInterface | undefined;
  isArchived: boolean;
  id: string;
}

interface NavbarOptimisticMessagePayload {
  projectId: string | undefined;
  messageGroupId: null;
  sender: UserModel | null;
  message: string;
  createdOn: string;
  isRead: boolean;
  uploadedFile: CommentFilesInterface[];
  reactions: null;
  reply: ReplyMessageDataInterface | Record<string, never>;
  isArchived: boolean;
  id: string;
}

interface UseMessagePayloadBuilderReturn {
  buildSendPayload: (params: BuildSendPayloadParams) => SendMessagePayload;
  buildUpdatePayload: (params: BuildUpdatePayloadParams) => UpdateMessagePayload;
  buildOptimisticMessage: (params: BuildOptimisticMessageParams) => OptimisticMessagePayload;
  buildNavbarOptimisticMessage: (params: BuildNavbarOptimisticMessageParams) => NavbarOptimisticMessagePayload;
  buildReplyPayload: (replyMessage: ReplyMessageDataInterface) => MessageReplyInterface | undefined;
}

interface BuildSendPayloadParams {
  commentData: string;
  currentProject: ProjectModel | ChatGroupInterface | null;
  isNavbarMessage: boolean;
  currentUser: UserModel | null;
  workspace: WorkspaceModel | null;
  uploadedFilesData: CommentFilesInterface[];
  replyMessage: ReplyMessageDataInterface;
  nanoId: string;
}

interface BuildUpdatePayloadParams {
  updatedHtmlString: string;
  currentProject: ProjectModel | ChatGroupInterface | null;
  isNavbarMessage: boolean;
  uploadedFilesData: CommentFilesInterface[];
}

interface BuildOptimisticMessageParams {
  commentData: string;
  currentProject: ProjectModel | ChatGroupInterface | null;
  currentUser: UserModel | null;
  uploadedFilesData: CommentFilesInterface[];
  replyMessage: ReplyMessageDataInterface;
  nanoId: string;
}

interface BuildNavbarOptimisticMessageParams {
  commentData: string;
  currentProject: ProjectModel | ChatGroupInterface | null;
  currentUser: UserModel | null;
  uploadedFilesData: CommentFilesInterface[];
  replyMessage: ReplyMessageDataInterface;
  nanoId: string;
}

export const useMessagePayloadBuilder = (): UseMessagePayloadBuilderReturn => {
  const buildReplyPayload = useCallback(
    (replyMessage: ReplyMessageDataInterface): MessageReplyInterface | undefined => {
      if (isEmpty(replyMessage)) return undefined;
      return {
        message: replyMessage?.Message || '',
        createdOn: replyMessage?.CreatedTime || '',
        files: replyMessage?.Files || [],
        id: replyMessage?.id || '',
        sender: replyMessage?.Sender || ''
      };
    },
    []
  );

  const buildSendPayload = useCallback(
    ({
      commentData,
      currentProject,
      isNavbarMessage,
      currentUser,
      workspace,
      uploadedFilesData,
      replyMessage,
      nanoId
    }: BuildSendPayloadParams): SendMessagePayload => {
      if (isNavbarMessage) {
        return {
          Message: commentData,
          ProjectId:
            (currentProject as ChatGroupInterface)?.type === CHAT_GROUP_TYPE.PROJECT ? currentProject?.id : undefined,
          ReadUsers: [currentUser?.id],
          UploadedFiles: uploadedFilesData,
          Reply: !isEmpty(replyMessage) ? replyMessage : null,
          MessageGroupId:
            (currentProject as ChatGroupInterface)?.type === CHAT_GROUP_TYPE.PERSONAL ? currentProject?.id : undefined,
          CompanyId: workspace?.id,
          _id: nanoId
        };
      }

      return {
        Message: commentData,
        ProjectId: currentProject?.id,
        ReadUsers: [currentUser?.id],
        UploadedFiles: uploadedFilesData,
        Reply: !isEmpty(replyMessage) ? replyMessage : null,
        _id: nanoId
      };
    },
    []
  );

  const buildUpdatePayload = useCallback(
    ({
      updatedHtmlString,
      currentProject,
      isNavbarMessage,
      uploadedFilesData
    }: BuildUpdatePayloadParams): UpdateMessagePayload => {
      return {
        Message: updatedHtmlString,
        UploadedFiles: uploadedFilesData,
        ProjectId:
          (currentProject as ChatGroupInterface)?.type === CHAT_GROUP_TYPE.PROJECT || !isNavbarMessage
            ? currentProject?.id
            : undefined,
        MessageGroupId:
          (currentProject as ChatGroupInterface)?.type === CHAT_GROUP_TYPE.PERSONAL && isNavbarMessage
            ? currentProject?.id
            : undefined
      };
    },
    []
  );

  const buildOptimisticMessage = useCallback(
    ({
      commentData,
      currentProject,
      currentUser,
      uploadedFilesData,
      replyMessage,
      nanoId
    }: BuildOptimisticMessageParams): OptimisticMessagePayload => {
      const replyPayload = buildReplyPayload(replyMessage);

      return {
        projectId: currentProject?.id ?? null,
        fileExt: '',
        messageGroupId: null,
        sender: {
          avatar: {
            avatar: currentUser?.avatar?.avatar || '',
            avatar_120: currentUser?.avatar?.avatar_120 || ''
          },
          email: currentUser?.email || '',
          id: currentUser?.id || '',
          name: currentUser?.name || '',
          phone: currentUser?.phone || '',
          profile_photo: currentUser?.profile_photo || '',
          isAdmin: currentUser?.isAdmin || false,
          isOwner: false
        },
        message: commentData,
        createdOn: new Date().toISOString(),
        isRead: true,
        uploadedFile: uploadedFilesData,
        reactions: [],
        reply: replyPayload,
        isArchived: false,
        id: nanoId
      };
    },
    [buildReplyPayload]
  );

  const buildNavbarOptimisticMessage = useCallback(
    ({
      commentData,
      currentProject,
      currentUser,
      uploadedFilesData,
      replyMessage,
      nanoId
    }: BuildNavbarOptimisticMessageParams): NavbarOptimisticMessagePayload => {
      const replyPayload = !isEmpty(replyMessage)
        ? {
            message: replyMessage?.Message,
            createdOn: replyMessage?.CreatedTime,
            files: replyMessage?.Files,
            id: replyMessage?.id,
            sender: replyMessage?.Sender
          }
        : {};

      return {
        projectId: currentProject?.id,
        messageGroupId: null,
        sender: currentUser,
        message: commentData,
        createdOn: new Date().toISOString(),
        isRead: true,
        uploadedFile: uploadedFilesData,
        reactions: null,
        reply: replyPayload,
        isArchived: false,
        id: nanoId
      };
    },
    []
  );

  return {
    buildSendPayload,
    buildUpdatePayload,
    buildOptimisticMessage,
    buildNavbarOptimisticMessage,
    buildReplyPayload
  };
};
